// src/components/Pages/SpecialOffers/QuickQuoteModal.js
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Combobox,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
  ComboboxPopover,
} from "@reach/combobox";
import "@reach/combobox/styles.css";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";

// --- utils ------------------------------------------------------------------
function validateField(v, rule, validationRules) {
  if (!rule) return null;
  if (rule.required && (v == null || String(v).trim() === ""))
    return "Required";
  if (
    typeof rule.minLength === "number" &&
    String(v).trim().length < rule.minLength
  )
    return "Too short";
  if (typeof rule.pattern === "string") {
    try {
      if (!new RegExp(rule.pattern).test(String(v))) return "Invalid format";
    } catch {}
  }
  if (typeof rule.min === "number") {
    const n = Number(v);
    if (!Number.isFinite(n) || n < rule.min) return "Too small";
  }
  if (typeof rule.max === "number") {
    const n = Number(v);
    if (!Number.isFinite(n) || n > rule.max) return "Too large";
  }
  
  // Apply vendor-specific validation rules
  if (rule.minAge !== undefined) {
    if (!v) return null;
    const birthDate = new Date(v);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    if (age < rule.minAge) {
      return `Must be at least ${rule.minAge} years old`;
    }
  }
  
  if (rule.minYearsFromToday !== undefined) {
    if (!v) return null;
    const inputDate = new Date(v);
    const today = new Date();
    const yearsDiff = today.getFullYear() - inputDate.getFullYear();
    if (yearsDiff < rule.minYearsFromToday) {
      return `Must be at least ${rule.minYearsFromToday} year(s) ago`;
    }
  }
  
  if (rule.maxDate !== undefined) {
    if (!v || rule.maxDate !== "today") return null;
    const inputDate = new Date(v);
    const today = new Date();
    if (inputDate > today) {
      return "Cannot be in the future";
    }
  }
  
  // Custom validation for license date vs date of birth
  if (rule.minAgeAtLicense !== undefined && validationRules?.dateOfBirth) {
    if (!v) return null;
    const licenseDate = new Date(v);
    const birthDate = new Date(validationRules.dateOfBirth);
    const today = new Date();
    
    // Check if license date is in the future
    if (licenseDate > today) {
      return "License date cannot be in the future";
    }
    
    // Calculate age at license issue
    let ageAtLicense = licenseDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = licenseDate.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && licenseDate.getDate() < birthDate.getDate())) {
      ageAtLicense--;
    }
    
    if (ageAtLicense < rule.minAgeAtLicense) {
      return `Driver must be at least ${rule.minAgeAtLicense} years old when license was issued`;
    }
  }
  
  return null;
}
const money = (v) =>
  v == null || v === "" ? "—" : "R " + Number(v).toLocaleString();

// --- Address Autocomplete ---------------------------------------------------
const AddressAutocomplete = ({ isMapsReady, setVal, label, setAddressSelected }) => {
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
    requestOptions: {
      componentRestrictions: { country: "za" }, // SA only
    },
  });

  const inputRef = useRef(null);
  const mapsReady =
    isMapsReady || localStorage.getItem("gmaps_loaded") === "true";

  const handleSelect = async (address) => {
    setValue(address, false);
    clearSuggestions();
    setVal("riskAddressFull", address);
    setAddressSelected(true);

    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      const comps = results[0]?.address_components || [];
      const getComp = (types) =>
        comps.find((c) => types.every((t) => c.types.includes(t)))?.long_name ||
        "";

      const streetNumber = getComp(["street_number"]);
      const route = getComp(["route"]);
      let line = `${streetNumber} ${route}`.trim();
      
      // If no street number/route found, try to extract from formatted address
      if (!line || line === "") {
        const addressParts = address.split(",");
        if (addressParts.length > 0) {
          line = addressParts[0].trim(); // Use first part as street address
        }
      }
      
      const suburb =
        getComp(["sublocality", "sublocality_level_1"]) ||
        getComp(["locality"]);
      const postal = getComp(["postal_code"]);
      const province = getComp(["administrative_area_level_1"]);

      setVal("addressLine", line);
      setVal("suburb", suburb);
      setVal("postalCode", postal);
      setVal("province", province);
      setVal("riskAddress_lat", lat);
      setVal("riskAddress_lng", lng);

      requestAnimationFrame(() => {
        inputRef.current?.focus({ preventScroll: true });
      });
    } catch (err) {
      console.error("Geocode error:", err);
    }
  };

  if (!mapsReady) {
    return (
      <TextField
        fullWidth
        disabled
        value="Google Places not loaded"
        size="small"
        sx={{ mb: 1.5 }}
        InputLabelProps={{ shrink: true }}
      />
    );
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: "0.875rem", mb: 0.5, fontWeight: 500 }}>
        {label}
      </Typography>
      <Combobox onSelect={handleSelect}>
        <ComboboxInput
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={!ready}
          style={{
            width: "100%",
            padding: "8.5px 14px",
            fontSize: "0.875rem",
            border: "1px solid #c4c4c4",
            borderRadius: "4px",
            outline: "none",
            fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
          }}
          placeholder="Type here to search your address"
        />
        {status === "OK" && (
          <ComboboxPopover portal={true} style={{ zIndex: 1400 }}>
            <ComboboxList>
              {data.map(({ place_id, description }) => (
                <ComboboxOption key={place_id} value={description} />
              ))}
            </ComboboxList>
          </ComboboxPopover>
        )}
      </Combobox>
    </Box>
  );
};

// --- Vehicle Search Field ---------------------------------------------------
const SearchField = memo(function SearchField({
  name,
  label,
  error,
  initialValue,
  onFocusCapture,
  requestVehicleSearch,
  vehicleResults,
  searchingVehicles,
  pickVehicle,
}) {
  const [searchText, setSearchText] = useState(() =>
    String(initialValue || "")
  );
  const [showResults, setShowResults] = useState(false);
  const inputRef = useRef(null);
  const skipSearchRef = useRef(false);

  useEffect(() => {
    if (!skipSearchRef.current) {
      requestVehicleSearch(searchText);
      setShowResults(true);
    }
    skipSearchRef.current = false;
  }, [searchText, requestVehicleSearch]);

  return (
    <Box sx={{ mb: 1.5 }}>
      <TextField
        fullWidth
        label={label || "Search"}
        value={searchText}
        inputRef={inputRef}
        onFocus={onFocusCapture}
        onChange={(e) => setSearchText(e.target.value)}
        size="small"
        error={!!error}
        helperText={error || "Type to search for your vehicle"}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
        InputLabelProps={{ shrink: true }}
        inputProps={{ autoComplete: 'off' }}
      />
      {searchingVehicles && <LinearProgress sx={{ mt: 1 }} />}
      {!searchingVehicles && vehicleResults.length > 0 && showResults && (
        <Box
          sx={{
            mt: 1,
            maxHeight: 220,
            overflowY: "auto",
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: "6px",
            bgcolor: "background.paper",
          }}
        >
          {vehicleResults.map((r) => {
            const key =
              String(r.vehicleid ?? r.vehicleId ?? "") +
              "-" +
              String(r.mmcode ?? "");
            const title = `${r.year ? r.year + " " : ""}${r.make ? r.make + " " : ""}${r.model || ""}`;
            return (
              <Box
                key={key}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setShowResults(false);
                  pickVehicle(r, (titleText) => {
                    skipSearchRef.current = true;
                    setSearchText(titleText);
                    requestAnimationFrame(() => {
                      const el = inputRef.current;
                      if (el) {
                        el.focus({ preventScroll: true });
                        try {
                          const len = el.value.length;
                          el.setSelectionRange(len, len);
                        } catch {}
                      }
                    });
                  });
                }}
                sx={{
                  px: 1,
                  py: 0.75,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "grey.50" },
                  borderBottom: "1px solid",
                  borderColor: "grey.100",
                }}
              >
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  {title}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                >
                  MM: {r.mmcode} • Fuel: {r.fueltype || "-"} • Category:{" "}
                  {r.category || "-"}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
});

// --- Generic Field (restores types) -----------------------------------------
const GenericField = memo(function GenericField({
  def,
  error,
  defaultValue,
  onFocusCapture,
  setVal,
  inputRefs,
  formValues,
  addressSelected,
}) {
  const nm = def.name;
  const setRef = (el) => {
    if (el) inputRefs.current[nm] = el;
  };

  if (def.type === "hidden") return null;

  // Check conditional rendering
  if (def.conditional) {
    const conditionField = def.conditional.field;
    const conditionValue = def.conditional.value;
    if (conditionField === "riskAddressSearch" && conditionValue === "populated") {
      if (!addressSelected) return null;
    } else {
      const currentValue = formValues[conditionField];
      if (currentValue !== conditionValue) return null;
    }
  }

  // SELECT
  if (def.type === "select") {
    const opts = Array.isArray(def.options) ? def.options : [];
    return (
      <TextField
        fullWidth
        select
        label={def.label || nm}
        defaultValue={defaultValue ?? ""}
        inputRef={setRef}
        onFocus={onFocusCapture}
        onChange={(e) => setVal(nm, e.target.value)}
        size="small"
        error={!!error}
        helperText={error || def.hint || ""}
        sx={{ mb: 1.5 }}
        InputLabelProps={{ shrink: true }}
      >
        {opts.map((o, idx) => {
          const value = o?.value ?? o?.id ?? o ?? "";
          const label = o?.label ?? String(value);
          const k = o?.value != null ? String(o.value) : String(idx);
          return (
            <MenuItem key={k} value={value}>
              {label}
            </MenuItem>
          );
        })}
      </TextField>
    );
  }

  // TOGGLE (Yes/No switch)
  if (def.type === "toggle") {
    const currentValue = formValues[nm] !== undefined ? formValues[nm] : (def.default !== undefined ? def.default : defaultValue);
    const isYes = currentValue === true || currentValue === "true";
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          gap: { xs: 1, sm: 2 }
        }}>
          <Typography sx={{ 
            fontSize: '0.9rem', 
            fontWeight: 500, 
            minWidth: 'fit-content',
            mb: { xs: 0.5, sm: 0 }
          }}>
            {def.label || nm}
          </Typography>
          <Box sx={{
            display: 'inline-flex',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            overflow: 'hidden',
            bgcolor: '#f5f5f5',
            minWidth: '120px',
            width: { xs: '100%', sm: 'auto' },
            maxWidth: '200px'
          }}>
            <Box
              component="button"
              type="button"
              onClick={() => setVal(nm, true)}
              onFocus={onFocusCapture}
              sx={{
                flex: 1,
                px: { xs: 3, sm: 2 },
                py: { xs: 1.5, sm: 1 },
                border: 'none',
                bgcolor: isYes ? '#21ad93' : 'transparent',
                color: isYes ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: { xs: '1rem', sm: '0.875rem' },
                fontWeight: 500,
                transition: 'all 0.2s ease',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: isYes ? '#1e9b82' : '#e0e0e0'
                },
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
            >
              Yes
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => setVal(nm, false)}
              onFocus={onFocusCapture}
              sx={{
                flex: 1,
                px: { xs: 3, sm: 2 },
                py: { xs: 1.5, sm: 1 },
                border: 'none',
                bgcolor: !isYes ? '#21ad93' : 'transparent',
                color: !isYes ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: { xs: '1rem', sm: '0.875rem' },
                fontWeight: 500,
                transition: 'all 0.2s ease',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: !isYes ? '#1e9b82' : '#e0e0e0'
                },
                '&:active': {
                  transform: 'scale(0.98)'
                }
              }}
            >
              No
            </Box>
          </Box>
        </Box>
        {error && (
          <Typography color="error" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  // CHECKBOX
  if (def.type === "checkbox") {
    return (
      <Box sx={{ mb: 2 }}>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <input
            type="checkbox"
            ref={setRef}
            defaultChecked={defaultValue === true || defaultValue === "true"}
            onFocus={onFocusCapture}
            onChange={(e) => setVal(nm, e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          <Typography>{def.label || nm}</Typography>
        </label>
        {error && (
          <Typography color="error" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  // BOOLEAN -> yes/no select
  if (def.type === "boolean") {
    return (
      <TextField
        fullWidth
        select
        label={def.label || nm}
        defaultValue={String(defaultValue ?? "")}
        inputRef={setRef}
        onFocus={onFocusCapture}
        onChange={(e) => setVal(nm, e.target.value === "true")}
        size="small"
        error={!!error}
        helperText={error || def.hint || ""}
        sx={{ mb: 1.5 }}
        InputLabelProps={{ shrink: true }}
      >
        <MenuItem value={"true"}>Yes</MenuItem>
        <MenuItem value={"false"}>No</MenuItem>
      </TextField>
    );
  }

  // NUMBER / CURRENCY
  if (def.type === "number" || def.type === "currency") {
    return (
      <TextField
        fullWidth
        type="number"
        label={def.label || nm}
        defaultValue={defaultValue ?? ""}
        inputRef={setRef}
        onFocus={onFocusCapture}
        onChange={(e) => setVal(nm, e.target.value)}
        size="small"
        error={!!error}
        helperText={error || def.hint || ""}
        sx={{ mb: 1.5 }}
        inputProps={{ min: 0 }}
        InputLabelProps={{ shrink: true }}
        InputProps={{ readOnly: !!def.readonly }}
      />
    );
  }

  // DATE
  if (def.type === "date") {
    return (
      <TextField
        fullWidth
        type="date"
        label={def.label || nm}
        defaultValue={defaultValue ?? ""}
        inputRef={setRef}
        onFocus={onFocusCapture}
        onChange={(e) => {
          setVal(nm, e.target.value);
          // Trigger validation for license date when it changes
          if (nm === 'licenseDate' && formValues.dateOfBirth) {
            const licenseDate = new Date(e.target.value);
            const birthDate = new Date(formValues.dateOfBirth);
            const today = new Date();
            
            if (licenseDate > today) {
              // Will be caught by validation
            } else {
              let ageAtLicense = licenseDate.getFullYear() - birthDate.getFullYear();
              const monthDiff = licenseDate.getMonth() - birthDate.getMonth();
              if (monthDiff < 0 || (monthDiff === 0 && licenseDate.getDate() < birthDate.getDate())) {
                ageAtLicense--;
              }
              // Validation will be triggered by the validation function
            }
          }
        }}
        size="small"
        error={!!error}
        helperText={error || def.hint || ""}
        sx={{ mb: 1.5 }}
        InputLabelProps={{ shrink: true }}
        InputProps={{ readOnly: !!def.readonly }}
      />
    );
  }

  // TEXT (default)
  return (
    <TextField
      fullWidth
      label={def.label || nm}
      defaultValue={defaultValue ?? ""}
      inputRef={setRef}
      onFocus={onFocusCapture}
      onChange={(e) => setVal(nm, e.target.value)}
      size="small"
      error={!!error}
      helperText={error || def.hint || ""}
      sx={{ mb: 1.5 }}
      InputLabelProps={{ shrink: true }}
      InputProps={{ readOnly: !!def.readonly }}
    />
  );
});

// --- Main -------------------------------------------------------------------
const QuickQuoteModal = ({
  open,
  onClose,
  selectedVendor,
  quickQuoteData,
  setQuickQuoteData,
  setQuickQuoteErrors,
  loading,
  setLoading,
  setCallbackRequests,
  setConfirmationMessage,
  setConfirmationIsError,
  setConfirmationOpen,
  isMobile,
  isMapsReady, // pass from App once
}) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const modalStyle = {
    position: "absolute",
    top: isMobile ? "0" : "50%",
    left: isMobile ? "0" : "50%",
    transform: isMobile ? "none" : "translate(-50%, -50%)",
    width: isMobile ? "100vw" : "600px",
    height: isMobile ? "100vh" : "auto",
    maxWidth: isMobile ? "100vw" : "90vw",
    bgcolor: "background.paper",
    borderRadius: isMobile ? 0 : "12px",
    boxShadow: isMobile ? 0 : "0 8px 32px rgba(0,0,0,0.12)",
    maxHeight: isMobile ? "100vh" : "85vh",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "rgba(0,0,0,0.05)",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#21ad93",
      borderRadius: "3px",
      opacity: 1,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#1e9b82",
    },
    boxShadow: isMobile ? 0 : "inset 0 -10px 10px -10px rgba(0,0,0,0.1)",
  };

  const [schema, setSchema] = useState(null);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const steps = useMemo(() => schema?.steps ?? [], [schema]);
  const [errors, setErrors] = useState({});
  const formRef = useRef({});
  const inputRefs = useRef({});
  const [formValues, setFormValues] = useState({});
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [searchingVehicles, setSearchingVehicles] = useState(false);
  const [vehicleResults, setVehicleResults] = useState([]);
  const [addressSelected, setAddressSelected] = useState(false);
  const searchTimer = useRef(null);

  const rootRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const onAnyFocus = useCallback((e) => {
    if (rootRef.current && rootRef.current.contains(e.target)) {
      lastFocusedRef.current = e.target;
    }
  }, []);
  useLayoutEffect(() => {
    if (!open) return;
    const el = document.activeElement;
    const inModal = rootRef.current && el && rootRef.current.contains(el);
    if (!inModal && lastFocusedRef.current) {
      lastFocusedRef.current.focus({ preventScroll: true });
    }
  });

  useEffect(() => {
    if (!open) return;
    document.body.classList.add("qq-modal-open");
    return () => document.body.classList.remove("qq-modal-open");
  }, [open]);

  useEffect(() => {
    if (!open || !selectedVendor?.id) return;
    setLoadingSchema(true);
    setSchema(null);
    setActiveStep(0);
    setErrors({});
    setVehicleResults([]);
    setSelectedVehicle(null);
    setAddressSelected(false);
    formRef.current = {};

    const loadFormSchema = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        
        const [schemaRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/quote/form-schema`, {
            params: { 
              vendorId: selectedVendor.id,
              userMobile: user?.mobile || user?.Mobile
            },
          })
        ]);

        const data = schemaRes.data || {};
        setSchema(data);

        const base = { ...(quickQuoteData || {}) };
        
        if (data.defaults && typeof data.defaults === "object") {
          Object.keys(data.defaults).forEach((k) => {
            if (typeof base[k] === "undefined") base[k] = data.defaults[k];
          });
        }
        
        data.steps?.forEach(step => {
          step.fields?.forEach(field => {
            if (field.default !== undefined && base[field.name] === undefined) {
              base[field.name] = field.default;
            }
          });
        });

        if (selectedVendor.slug === 'iwyze' && user?.IDNumber) {
          try {
            const defaultsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/quote/user-defaults`, {
              params: { 
                vendorId: selectedVendor.id,
                idNumber: user.IDNumber 
              },
              headers: { 'x-api-key': process.env.REACT_APP_BACKEND_API_KEY }
            });
            const userDefaults = defaultsRes.data || {};
            
            Object.entries(userDefaults).forEach(([key, value]) => {
              if (value && (base[key] === undefined || base[key] === '')) {
                base[key] = value;
              }
            });
          } catch (err) {
            // Silent fail
          }
        }
        
        // Clean up any address search values
        delete base.riskAddressSearch;
        
        formRef.current = base;
        setFormValues(base);
        setQuickQuoteData(base);
        setQuickQuoteErrors({});
      } catch (err) {
        setSchema({ steps: [] });
      } finally {
        setLoadingSchema(false);
      }
    };

    loadFormSchema();
  }, [open, selectedVendor?.id, selectedVendor?.slug]);

  const getVal = useCallback((n) => formRef.current?.[n] ?? "", []);
  const setVal = useCallback((n, v) => {
    formRef.current = { ...(formRef.current || {}), [n]: v };
    setFormValues(prev => ({ ...prev, [n]: v }));
    
    // Real-time validation for license date
    if (n === 'licenseDate' && formRef.current.dateOfBirth) {
      const licenseDate = new Date(v);
      const birthDate = new Date(formRef.current.dateOfBirth);
      const today = new Date();
      
      let errorMsg = null;
      
      if (licenseDate > today) {
        errorMsg = "License date cannot be in the future";
      } else if (v) {
        let ageAtLicense = licenseDate.getFullYear() - birthDate.getFullYear();
        const monthDiff = licenseDate.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && licenseDate.getDate() < birthDate.getDate())) {
          ageAtLicense--;
        }
        
        if (ageAtLicense < 18) {
          errorMsg = "Driver must be at least 18 years old when license was issued";
        }
      }
      
      setErrors(prev => ({
        ...prev,
        [n]: errorMsg
      }));
    } else {
      setErrors((prev) => {
        if (!prev[n]) return prev;
        const next = { ...prev };
        delete next[n];
        return next;
      });
    }
    
    const el = inputRefs.current?.[n];
    if (el && el.value !== undefined) el.value = v ?? "";
  }, []);

  const validateStep = useCallback(
    (stepDef) => {
      const errs = {};
      if (!stepDef?.fields?.length) return errs;

      for (const f of stepDef.fields) {
        const nm = f?.name;
        if (!nm) continue;
        
        // Skip validation for conditionally hidden fields
        if (f.conditional) {
          const conditionField = f.conditional.field;
          const conditionValue = f.conditional.value;
          if (conditionField === "riskAddressSearch" && conditionValue === "populated") {
            if (!addressSelected) continue;
          } else if (conditionField === "retailPrice" && conditionValue === "available") {
            const currentValue = formValues[conditionField];
            if (currentValue !== "available") continue;
          } else {
            const currentValue = formValues[conditionField];
            if (currentValue !== conditionValue) continue;
          }
        }
        
        // Special validation for address search
        if (f.type === "search_address" && f.validations?.required) {
          if (!addressSelected) {
            errs[nm] = "Please select an address";
          }
          continue;
        }
        
        const validationRules = { ...(schema?.validationRules || {}), ...formValues };
        const msg = validateField(getVal(nm), f.validations || null, validationRules);
        if (msg) errs[nm] = msg;
      }
      return errs;
    },
    [getVal, schema, formValues, addressSelected]
  );



  const requestVehicleSearch = useCallback((term) => {
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
      searchTimer.current = null;
    }
    const t = String(term || "");
    if (t.trim().length < 2) {
      setVehicleResults([]);
      return;
    }
    searchTimer.current = setTimeout(() => {
      setSearchingVehicles(true);
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/api/quote/vehicles`, {
          params: { search: t, limit: 20 },
        })
        .then((res) => {
          const data = res.data;
          setVehicleResults(Array.isArray(data?.items) ? data.items : []);
        })
        .catch(() => setVehicleResults([]))
        .finally(() => setSearchingVehicles(false));
    }, 200);
  }, []);

  const pickVehicle = useCallback(
    (row, setSearchTitle) => {
      const retail = row?.retailprice ?? row?.retailPrice ?? null;
      const market = row?.marketprice ?? row?.marketPrice ?? null;

      const updates = {
        vehicleSearch:
          `${row.year || ""} ${row.make || ""} ${row.model || ""}`.trim(),
        mmCode: String(row?.mmcode || ""),
        make: row?.make || "",
        model: row?.model || "",
        year: String(row?.year || ""),
        retailPrice: retail ? "available" : "",
        insuredValue: retail ? Math.round(retail) : "",
      };
      Object.entries(updates).forEach(([k, v]) => setVal(k, v));

      setSelectedVehicle({
        vehicleId: row.vehicleid ?? row.vehicleId ?? null,
        mmcode: row.mmcode ?? null,
        make: row.make ?? "",
        model: row.model ?? "",
        year: row.year ?? null,
        fuelType: row.fueltype ?? "",
        category: row.category ?? "",
        retailPrice: retail,
        marketPrice: market,
      });

      setSearchTitle(updates.vehicleSearch);
      setVehicleResults([]);
    },
    [setVal]
  );

  const syncParent = useCallback(() => {
    setQuickQuoteData((p) => ({ ...(p || {}), ...(formRef.current || {}) }));
    setQuickQuoteErrors(errors);
  }, [errors, setQuickQuoteData, setQuickQuoteErrors]);

  function extractMonthlyPremium(resp) {
    if (!resp) return null;

    // straight fields first
    if (resp.monthlyPremium != null) return Number(resp.monthlyPremium);
    if (resp.premium != null) return Number(resp.premium);

    // common wrappers
    const r = resp.resource || resp.data || null;
    if (!r) return null;

    if (r.totalPremium != null) return Number(r.totalPremium);
    if (Array.isArray(r.quoteResults) && r.quoteResults[0]?.premium != null) {
      return Number(r.quoteResults[0].premium);
    }
    return null;
  }

  const handleSubmitQuickQuote = useCallback(() => {
    syncParent();
    setLoading(true);
    
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const requestData = {
      ...formRef.current,
      userId: user.id,
      userUuid: user.uuid
    };
    
    axios
      .post(
        `${process.env.REACT_APP_BACKEND_URL}/api/quote/quick-quote`,
        requestData,
        { 
          params: { vendorId: selectedVendor?.id || "" },
          headers: {
            'x-user-id': user.id,
            'x-user-uuid': user.uuid
          }
        }
      )
      .then((res) => {
        const api = res?.data || {};
        const f = formRef.current || {};

        // 🔎 Pull monthly premium from any of the known places
        const premiumNum = extractMonthlyPremium(api);

        // Prefer external/quote reference IDs from iWyze, else our own
        const r = api.resource || api.data || {};
        const callbackRef =
          r.externalReferenceId || r.quoteReferenceId || "ref-" + Date.now();

        const vehicleDetails = `${f.year ? f.year + " " : ""}${f.make ? f.make + " " : ""}${f.model ? String(f.model).trim() : ""}`;

        const sumInsured =
          typeof f?.insuredValue === "number"
            ? "R " + f.insuredValue.toLocaleString()
            : f?.insuredValue
              ? "R " + Number(f.insuredValue).toLocaleString()
              : "—";

        setCallbackRequests((prev) => ({
          ...prev,
          [selectedVendor?.id || "vendor"]: {
            createdAt: new Date(),
            id: callbackRef, // keep the vendor ref so we can pass it to callback
          },
        }));

        onClose();
        setLoading(false);
        setConfirmationMessage({
          vehicleDetails,
          sumInsured,
          coverType: f?.coverCode || "Comprehensive",
          monthlyPremium:
            typeof premiumNum === "number"
              ? "R " + premiumNum.toLocaleString()
              : "R 1723.55",
          callbackRef, // <-- make available to the Accept flow
          selectedVendor, // <-- preserve selectedVendor
          // Include form values for quote acceptance
          firstName: f?.firstName,
          lastName: f?.lastName,
          contactNumber: f?.alternativeContactNumber || user?.mobile,
          email: f?.email || user?.email,
          idNumber: user?.IDNumber
        });
        setConfirmationIsError(false);
        setConfirmationOpen(true);
      })
      .catch((err) => {
        onClose();
        setLoading(false);
        setConfirmationMessage({
          message: String(
            err?.response?.data?.error || err?.message || "Error"
          ),
        });
        setConfirmationIsError(true);
        setConfirmationOpen(true);
      });
  }, [
    onClose,
    selectedVendor?.id,
    setCallbackRequests,
    setConfirmationMessage,
    setConfirmationIsError,
    setConfirmationOpen,
    setLoading,
    syncParent,
  ]);

  const renderField = (def) => {
    if (!def || def.type === "hidden") return null;
    const nm = def.name;
    const err = errors?.[nm];

    // Check conditional rendering
    if (def.conditional) {
      const conditionField = def.conditional.field;
      const conditionValue = def.conditional.value;
      if (conditionField === "riskAddressSearch" && conditionValue === "populated") {
        if (!addressSelected) return null;
      } else if (conditionField === "retailPrice" && conditionValue === "available") {
        const currentValue = formValues[conditionField];
        if (currentValue !== "available") return null;
      } else {
        const currentValue = formValues[conditionField];
        if (currentValue !== conditionValue) return null;
      }
    }

    if (def.type === "search") {
      return (
        <>
          <SearchField
            key={nm}
            name={nm}
            label={def.label}
            error={err}
            initialValue={getVal(nm)}
            onFocusCapture={onAnyFocus}
            requestVehicleSearch={requestVehicleSearch}
            vehicleResults={vehicleResults}
            searchingVehicles={searchingVehicles}
            pickVehicle={pickVehicle}
          />
          {selectedVehicle && (
            <Paper
              variant="outlined"
              sx={{ mt: 0.5, mb: 1.5, borderRadius: "8px", overflow: "hidden" }}
            >
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>
                      {selectedVehicle.year} {selectedVehicle.make}{" "}
                      {selectedVehicle.model}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>MM Code</TableCell>
                    <TableCell>{selectedVehicle.mmcode}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Fuel Type</TableCell>
                    <TableCell>{selectedVehicle.fuelType || "—"}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      );
    }

    if (def.type === "divider") {
      return (
        <Box key={nm} sx={{ mt: 1.5, mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 0.5, fontSize: '1rem' }}>
            {def.label}
          </Typography>
          <Divider sx={{ mb: 1 }} />
        </Box>
      );
    }

    if (def.type === "text_body") {
      return (
        <Typography key={nm} sx={{ mb: 1, color: '#666', fontSize: '0.9rem' }}>
          {def.label}
        </Typography>
      );
    }

    if (def.type === "search_address") {
      return (
        <Box key={nm}>
          <AddressAutocomplete
            isMapsReady={true}
            setVal={setVal}
            label={def.label}
            setAddressSelected={setAddressSelected}
          />
          {err && (
            <Typography color="error" sx={{ fontSize: '0.75rem', mt: -1, mb: 1.5 }}>
              {err}
            </Typography>
          )}
        </Box>
      );
    }

    // Make the risk address sub-fields read-only and hide vehicle year for iWyze
    const forceReadonly =
      nm === "addressLine" || nm === "suburb" || nm === "postalCode" || 
      (selectedVendor?.slug === 'iwyze' && nm === "year")
        ? { readonly: true }
        : {};
    
    // Hide vehicle year field for iWyze
    if (selectedVendor?.slug === 'iwyze' && nm === "year") {
      return null;
    }
    const mergedDef = { ...def, ...forceReadonly };

    if (
      ["text", "number", "currency", "select", "date", "boolean", "toggle", "checkbox"].includes(
        mergedDef.type
      )
    ) {
      return (
        <GenericField
          key={nm}
          def={mergedDef}
          error={err}
          defaultValue={getVal(nm)}
          onFocusCapture={onAnyFocus}
          setVal={setVal}
          inputRefs={inputRefs}
          formValues={formValues}
          addressSelected={addressSelected}
        />
      );
    }

    return null;
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box ref={rootRef} sx={modalStyle}>
        {!loading && (
          <IconButton 
            onClick={handleClose}
            sx={{ 
              position: 'absolute',
              top: 16,
              right: 16,
              color: "#666",
              zIndex: 1
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
        
        <Box sx={{ p: 3, pb: 0 }}>
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            {schema?.logoUrl && (
              <Box sx={{ mb: 2 }}>
                <img 
                  src={schema.logoUrl} 
                  alt={schema?.vendor || 'Vendor'} 
                  style={{ maxHeight: '100px', width: 'auto' }}
                />
              </Box>
            )}
            <Typography variant="h5" sx={{ fontWeight: 600, color: "#333" }}>
              {schema?.title || 'Get Your Quote'}
            </Typography>
            {selectedVendor?.slug === 'iwyze' && (
              <Box sx={{ 
                mt: 1.5, 
                p: 1.5, 
                bgcolor: '#f5f5f5', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}>
                <InfoIcon sx={{ color: '#000', fontSize: '1.1rem' }} />
                <Typography sx={{ 
                  fontSize: '0.875rem', 
                  color: '#444',
                  fontWeight: 500
                }}>
                  The premium shown in this quotation is indicative only and based on the information currently available. The final premium may change and will depend on the confirmed risk profile. Terms and conditions apply.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {loadingSchema ? (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ px: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                {steps.map((s, idx) => (
                  <Box key={idx} sx={{ flex: 1, height: 4, bgcolor: idx <= activeStep ? '#21ad93' : '#e0e0e0', borderRadius: 2 }} />
                ))}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#333' }}>
                {steps[activeStep]?.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                Step {activeStep + 1} of {steps.length}
              </Typography>
            </Box>

            <Box sx={{ px: 3, flex: 1 }}>
              {steps.map((s, idx) => {
                if (idx !== activeStep) return null;
                return (
                  <Box key={idx} sx={{ pb: 2 }}>
                    <Box sx={{ display: 'grid', gap: 2 }}>
                      {s.fields?.map((f) => renderField(f))}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ p: 3, pt: 2, borderTop: '1px solid #f0f0f0', mt: 'auto' }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button 
                  onClick={handleClose}
                  disabled={loading}
                  sx={{ color: '#666', textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {activeStep > 0 && (
                    <Button
                      onClick={() => setActiveStep((p) => p - 1)}
                      variant="outlined"
                      sx={{ textTransform: 'none', borderColor: '#e0e0e0' }}
                    >
                      Back
                    </Button>
                  )}
                  {activeStep < steps.length - 1 ? (
                    <Button
                      key={`continue-${addressSelected}`}
                      variant="contained"
                      disabled={(() => {
                        if (!steps[activeStep]) return true;
                        const errs = validateStep(steps[activeStep]);
                        return Object.keys(errs).length > 0;
                      })()}
                      onClick={() => {
                        const errs = validateStep(steps[activeStep]);
                        setErrors(errs);
                        if (Object.keys(errs).length > 0) {
                          return;
                        }
                        setActiveStep((p) => p + 1);
                      }}
                      sx={{ bgcolor: '#21ad93', color: 'white', textTransform: 'none', px: 3, '&:hover': { bgcolor: '#1e9b82' } }}
                    >
                      Continue
                    </Button>
                  ) : (
                    <Button
                      key={`submit-${addressSelected}`}
                      variant="contained"
                      disabled={loading || (() => {
                        if (!steps[activeStep]) return true;
                        const errs = validateStep(steps[activeStep]);
                        return Object.keys(errs).length > 0;
                      })()}
                      onClick={() => {
                        const errs = validateStep(steps[activeStep]);
                        setErrors(errs);
                        if (Object.keys(errs).length > 0) {
                          return;
                        }
                        handleSubmitQuickQuote();
                      }}
                      sx={{ bgcolor: '#21ad93', color: 'white', textTransform: 'none', px: 3, '&:hover': { bgcolor: '#1e9b82' } }}
                    >
                      {loading ? "Getting Quote..." : "Get Quote"}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default QuickQuoteModal;
